<script setup lang="ts">
import { computed, nextTick, onMounted, ref, useTemplateRef, watch } from "vue";
import { useRoute } from "vue-router";

import ReportMetrics from "@/components/datascience/ReportMetrics.vue";
import TableReport, { type TableReportDto } from "@/components/datascience/TableReport.vue";
import { MediaType, type EstimatorReportDto } from "@/dto";
import TwoColumnsLayout from "@/layouts/TwoColumnsLayout.vue";
import { useDatasourceStore } from "@/stores/datasource";
import ReportModelPipeline from "@/views/reports/ReportModelPipeline.vue";
import ReportSummary from "@/views/reports/ReportSummary.vue";
import { useReportScrolling } from "@/views/reports/useReportScrolling";

const { report } = defineProps<{
  report: EstimatorReportDto;
}>();

const datasource = useDatasourceStore();

const tableReportSpec = ref<TableReportDto | null>(null);

const tableReport = useTemplateRef<InstanceType<typeof TableReport>>("tableReport");
const twoColumnsLayout = useTemplateRef<InstanceType<typeof TwoColumnsLayout>>("twoColumnsLayout");
const reportMetrics = useTemplateRef<InstanceType<typeof ReportMetrics>>("reportMetrics");
const reportModelPipeline =
  useTemplateRef<InstanceType<typeof ReportModelPipeline>>("reportModelPipeline");

// Set up scrolling functionality
const { scrollToAnchor } = useReportScrolling({
  twoColumnsLayoutRef: twoColumnsLayout,
  tableReportRef: tableReport,
  reportMetricsRef: reportMetrics,
  reportModelPipelineRef: reportModelPipeline,
});

const route = useRoute();

const tableReportMedia = computed(() => {
  const reports = report.medias.filter(
    (m) => m.category === "data" && m.content_type === MediaType.TableReport
  );
  return reports.length > 1
    ? reports.find((r) => r.data_source === datasource.sourceName)
    : reports[0];
});

async function fetchTableReport() {
  if (tableReportMedia.value) {
    const response = await fetch(tableReportMedia.value.presigned_url);
    tableReportSpec.value = (await response.json()) as TableReportDto;
  } else {
    tableReportSpec.value = null;
  }
}

watch([() => report, () => datasource.sourceName], async () => {
  await fetchTableReport();
});

onMounted(async () => {
  await fetchTableReport();

  // Handle initial page load with anchor after data is fetched
  if (route.hash) {
    await nextTick();
    scrollToAnchor(route.hash);
  }
});
</script>

<template>
  <div class="estimator-report">
    <TwoColumnsLayout ref="twoColumnsLayout">
      <template #leftPrelude>
        <slot name="summaryPrelude" />
      </template>

      <template #left>
        <ReportSummary :report="report" />
      </template>

      <template #rightPrelude>
        <slot name="contentPrelude" />
      </template>

      <template #right>
        <div class="cards">
          <div id="table-report">
            <TableReport v-if="tableReportSpec" ref="tableReport" :spec="tableReportSpec">
              <template #actions></template>
            </TableReport>
          </div>

          <div id="general-metrics">
            <ReportMetrics ref="reportMetrics" :report="report" />
          </div>

          <div id="pipeline">
            <ReportModelPipeline ref="reportModelPipeline" :report="report" />
          </div>
        </div>
      </template>
    </TwoColumnsLayout>
  </div>
</template>

<style scoped>
.estimator-report {
  display: flex;
  height: 100%;
  min-height: 0;
  flex-direction: column;

  .cards {
    display: flex;
    flex-direction: column;
    padding-bottom: var(--numbers-32);
    gap: var(--numbers-32);
  }
}
</style>
